// ============================================================
// turnTrackerRolls - initiative dice for the turn tracker.
//
// The three places the tracker rolls initiative (admission, a
// reroll, a round that redraws the order) all need the same two
// lookups - the sheet behind a combatant and what the table adds on
// top of it. They live here once so the three cannot drift.
//
// No React: the hook hands in its lookups, these return values.
// ============================================================

import type { StoredInitiative } from '../engine/roster/hidden';
import { rollInitiative } from '../engine/initiative';
import type { InitiativeRoll } from '../engine/initiative';
import type { InitiativeAssignment } from '../engine/turnTracker';
import type { Character } from '../engine/character/types';
import { createParticipant } from '../engine/factories';
import type { TurnParticipant } from '../types';

/** Where a roll's sheet and table bonus come from. Either may be absent: a bare d10. */
export interface InitiativeSources {
  sheetFor?: (combatantId: string) => Character | null | undefined;
  initiativeModFor?: (combatantId: string) => number;
}

/** What the table adds to this combatant's roll - nothing for a custom entry. */
function tableModFor(sources: InitiativeSources, combatantId: string | null): number {
  return combatantId ? (sources.initiativeModFor?.(combatantId) ?? 0) : 0;
}

/**
 * Roll for a participant already in the order.
 *
 * Looks the sheet up; a custom participant (no combatant) rolls a bare d10.
 */
function rollForParticipant(sources: InitiativeSources, combatantId: string | null): InitiativeRoll {
  return rollInitiative(combatantId ? sources.sheetFor?.(combatantId) : null, tableModFor(sources, combatantId));
}

/** A fresh roll for each of these participants, in their order - dice are consumed in that order. */
export function rollAssignments(
  participants: readonly TurnParticipant[],
  sources: InitiativeSources,
): InitiativeAssignment[] {
  return participants.map((p) => {
    const { roll, bonus } = rollForParticipant(sources, p.combatantId);
    return { participantId: p.id, roll, bonus };
  });
}

/**
 * The sheet an arrival rolls from. A handed-in sheet WINS, even a null
 * one: `sheetFor` reads a ref that lags a combatant admitted in the same
 * tick as it was created. `undefined` means "not handed in".
 */
function arrivalSheet(
  sources: InitiativeSources,
  combatantId: string | null,
  sheet: Character | null | undefined,
): Character | null | undefined {
  if (sheet !== undefined) return sheet;
  return combatantId ? sources.sheetFor?.(combatantId) : null;
}

/**
 * One new participant, its initiative already rolled when `autoRoll` is on.
 *
 * With `autoRoll` off the participant is returned exactly as the factory
 * made it - initiative unrolled, no dice consumed.
 */
export function rolledParticipant(
  arrival: {
    name: string;
    combatantId: string | null;
    isCustom?: boolean;
    sheet?: Character | null;
    initiative?: StoredInitiative | null;
  },
  sources: InitiativeSources & { autoRoll: boolean },
): TurnParticipant {
  const { name, combatantId, isCustom = false, sheet, initiative: kept } = arrival;
  const p = createParticipant(name, combatantId, isCustom);
  // A kept initiative (a revealed figure's) is restored, never rerolled - and no die is consumed (gotcha 5).
  if (kept?.value != null)
    return { ...p, initiative: kept.value, initiativeRoll: kept.roll, initiativeBonus: kept.bonus };
  if (!sources.autoRoll) return p;
  const resolved = arrivalSheet(sources, combatantId, sheet);
  const { roll, bonus, total } = rollInitiative(resolved, tableModFor(sources, combatantId));
  return { ...p, initiative: total, initiativeRoll: roll, initiativeBonus: bonus };
}
